import { Role } from "@/constants/enums/user/role";
import {
  SystemUserAccessRole,
  SystemUserAccessRoleValues,
} from "@/constants/enums/user/systemUserAccessRole";
import type { User } from "@/types/auth.types";

export const AUTH_USER_SCHEMA_VERSION = 2;

export const SUPER_ADMIN_ONLY_ADMIN_PATHS = [
  "/admin/record-sets",
  "/admin/roles",
] as const;

/** User or subset with optional raw API role fields used during login normalization */
export type AuthUserInput =
  | User
  | (Pick<User, "access_role" | "role" | "login_role"> & {
      api_role?: string;
      platform_role?: string;
      user_role?: string;
    })
  | null
  | undefined;

const parseAccessRoleLabel = (label: string): SystemUserAccessRole | null => {
  switch (label) {
    case "super admin":
      return SystemUserAccessRole.SUPER_ADMIN;
    case "admin":
      return SystemUserAccessRole.ADMIN;
    case "verifier":
      return SystemUserAccessRole.VERIFIER;
    default:
      return null;
  }
};

const parseAccessRole = (value: unknown): SystemUserAccessRole | null => {
  if (typeof value === "number" && Number.isFinite(value)) {
    if (
      value === SystemUserAccessRole.SUPER_ADMIN ||
      value === SystemUserAccessRole.ADMIN ||
      value === SystemUserAccessRole.VERIFIER
    ) {
      return value;
    }
    return null;
  }

  if (typeof value === "string" && value.trim()) {
    const mappedLabelRole = parseAccessRoleLabel(value.trim().toLowerCase());
    if (mappedLabelRole !== null) {
      return mappedLabelRole;
    }

    const parsed = Number.parseInt(value, 10);
    if (!Number.isNaN(parsed)) {
      return parseAccessRole(parsed);
    }
  }

  return null;
};

/** Raw platform role from login API (before session normalization). */
export const extractRawPlatformRole = (user: AuthUserInput): string => {
  if (!user) return "";

  const { login_role, api_role, platform_role, user_role, role } = user as Pick<
    User,
    "login_role" | "role"
  > & {
    api_role?: string;
    platform_role?: string;
    user_role?: string;
  };

  const candidates = [
    login_role,
    api_role,
    platform_role,
    user_role,
    typeof role === "string" ? role : null,
  ];

  for (const candidate of candidates) {
    if (typeof candidate === "string" && candidate.trim()) {
      return candidate.trim();
    }
  }

  if (role === Role.SUPER_ADMIN) return Role.SUPER_ADMIN;
  if (role === Role.ADMIN) return Role.ADMIN;
  if (role === Role.VERIFIER) return Role.VERIFIER;

  return "";
};

const isLegacyFullAdminPlatformRole = (rawPlatformRole: string): boolean => {
  const normalized = rawPlatformRole.trim().toLowerCase();
  return normalized === "admin" || normalized === "super admin";
};

/**
 * Resolves platform access.
 * - Former login role "Admin" → Super Admin (full modules), unless access_role is explicitly 2 (new limited Admin).
 * - access_role 1 → Super Admin, 2 → limited Admin, 3 → Verifier.
 */
export const resolveAccessRole = (
  user: AuthUserInput,
): SystemUserAccessRole | null => {
  if (!user) return null;

  const parsedAccessRole = parseAccessRole(user.access_role);
  const rawPlatformRole = extractRawPlatformRole(user);

  // New limited Admin — must be explicit access_role 2
  if (parsedAccessRole === SystemUserAccessRole.ADMIN) {
    return SystemUserAccessRole.ADMIN;
  }

  // Former full Admin users (login role "Admin") → Super Admin
  if (isLegacyFullAdminPlatformRole(rawPlatformRole)) {
    return SystemUserAccessRole.SUPER_ADMIN;
  }

  if (parsedAccessRole === SystemUserAccessRole.SUPER_ADMIN) {
    return SystemUserAccessRole.SUPER_ADMIN;
  }

  if (parsedAccessRole === SystemUserAccessRole.VERIFIER) {
    return SystemUserAccessRole.VERIFIER;
  }

  const rawNormalized = rawPlatformRole.trim().toLowerCase();
  if (rawNormalized === "verifier") {
    return SystemUserAccessRole.VERIFIER;
  }

  if (user.role === Role.SUPER_ADMIN) {
    return SystemUserAccessRole.SUPER_ADMIN;
  }

  if (user.role === Role.ADMIN) {
    return SystemUserAccessRole.ADMIN;
  }

  if (user.role === Role.VERIFIER) {
    return SystemUserAccessRole.VERIFIER;
  }

  return null;
};

export const getAccessRoleLabel = (user: AuthUserInput): string => {
  const accessRole = resolveAccessRole(user);
  if (accessRole === null) {
    return extractRawPlatformRole(user) || String(user?.role ?? "");
  }
  return SystemUserAccessRoleValues[accessRole];
};

export const accessRoleToSessionRole = (
  accessRole: SystemUserAccessRole,
): Role => {
  switch (accessRole) {
    case SystemUserAccessRole.SUPER_ADMIN:
      return Role.SUPER_ADMIN;
    case SystemUserAccessRole.ADMIN:
      return Role.ADMIN;
    case SystemUserAccessRole.VERIFIER:
      return Role.VERIFIER;
    default:
      return Role.SUPER_ADMIN;
  }
};

export const normalizeAuthUser = (
  apiUser: User & {
    api_role?: string;
    platform_role?: string;
    user_role?: string;
  },
): User => {
  const rawPlatformRole = extractRawPlatformRole(apiUser);
  const resolvedAccessRole =
    resolveAccessRole({
      ...apiUser,
      login_role: rawPlatformRole || apiUser.login_role,
    }) ??
    (isLegacyFullAdminPlatformRole(rawPlatformRole)
      ? SystemUserAccessRole.SUPER_ADMIN
      : null);

  const accessRole =
    resolvedAccessRole ??
    (parseAccessRole(apiUser.access_role) === SystemUserAccessRole.VERIFIER ||
    extractRawPlatformRole(apiUser).toLowerCase() === "verifier"
      ? SystemUserAccessRole.VERIFIER
      : SystemUserAccessRole.SUPER_ADMIN);

  return {
    ...apiUser,
    login_role: rawPlatformRole || apiUser.login_role,
    access_role: accessRole,
    role: accessRoleToSessionRole(accessRole),
    auth_schema_version: AUTH_USER_SCHEMA_VERSION,
  };
};

export const isSuperAdminUser = (user: AuthUserInput): boolean =>
  resolveAccessRole(user) === SystemUserAccessRole.SUPER_ADMIN;

/** Only Super Admin can create or assign platform Admin users */
export const canManagePlatformAdminUsers = (user: AuthUserInput): boolean =>
  isSuperAdminUser(user);

export const canAssignAccessRole = (
  actor: AuthUserInput,
  targetAccessRole: SystemUserAccessRole | number | null | undefined,
): boolean => {
  const parsedTarget = parseAccessRole(targetAccessRole);
  if (parsedTarget === null) {
    return false;
  }
  if (parsedTarget === SystemUserAccessRole.VERIFIER) {
    return hasAdminPortalAccess(actor);
  }
  if (parsedTarget === SystemUserAccessRole.ADMIN) {
    return canManagePlatformAdminUsers(actor);
  }
  if (parsedTarget === SystemUserAccessRole.SUPER_ADMIN) {
    return false;
  }
  return false;
};

export const isLimitedAdminUser = (user: AuthUserInput): boolean =>
  resolveAccessRole(user) === SystemUserAccessRole.ADMIN;

export const hasAdminPortalAccess = (user: AuthUserInput): boolean => {
  const accessRole = resolveAccessRole(user);
  return (
    accessRole === SystemUserAccessRole.SUPER_ADMIN ||
    accessRole === SystemUserAccessRole.ADMIN
  );
};

export const isVerifierUser = (user: AuthUserInput): boolean =>
  resolveAccessRole(user) === SystemUserAccessRole.VERIFIER;

export const isSuperAdminOnlyAdminPath = (pathname: string): boolean =>
  SUPER_ADMIN_ONLY_ADMIN_PATHS.some(
    (restrictedPath) =>
      pathname === restrictedPath || pathname.startsWith(`${restrictedPath}/`),
  );

export const canAccessAdminPath = (
  user: AuthUserInput,
  pathname: string,
): boolean => {
  if (!hasAdminPortalAccess(user)) {
    return false;
  }

  if (isSuperAdminOnlyAdminPath(pathname) && !isSuperAdminUser(user)) {
    return false;
  }

  return true;
};

export const getPostLoginPath = (user: AuthUserInput): string =>
  hasAdminPortalAccess(user) ? "/admin/dashboard" : "/verifier/work-queue";
